import { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Typography,
  Skeleton,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { trpc } from '../../lib/trpc';

/**
 * Format date for display
 */
function formatDate(date: Date | string | null): string {
  if (!date) return 'Never';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get status chip color
 */
function getStatusColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  switch (status) {
    case 'UP':
      return 'success';
    case 'DOWN':
      return 'error';
    case 'PENDING':
      return 'warning';
    case 'PAUSED':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get monitor type chip color
 */
function getTypeColor(type: string): 'primary' | 'secondary' | 'info' | 'default' {
  switch (type) {
    case 'HTTP':
    case 'HTTPS':
      return 'primary';
    case 'PING':
      return 'secondary';
    case 'TCP':
      return 'info';
    case 'KEYWORD':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Admin monitors table with search, filter, and pagination
 */
export function AdminMonitorsTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearch(value);
    // Simple debounce using setTimeout
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 300);
  };

  // Query monitors
  const { data, isLoading, isError } = trpc.admin.getMonitors.useQuery({
    page: page + 1,
    limit: rowsPerPage,
    search: debouncedSearch || undefined,
    status: statusFilter ? (statusFilter as 'UP' | 'DOWN' | 'PENDING' | 'PAUSED') : undefined,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isError) {
    return (
      <Typography color="error">
        Failed to load monitors. Please try again.
      </Typography>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Filters */}
      <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name, URL, or owner..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="UP">Up</MenuItem>
            <MenuItem value="DOWN">Down</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="PAUSED">Paused</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Check</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                </TableRow>
              ))
            ) : data?.monitors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No monitors found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.monitors.map((monitor) => (
                <TableRow key={monitor.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {monitor.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={monitor.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        maxWidth: 250,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {monitor.url}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={monitor.type}
                      size="small"
                      color={getTypeColor(monitor.type)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={monitor.status}
                      size="small"
                      color={getStatusColor(monitor.status)}
                    />
                  </TableCell>
                  <TableCell>{monitor.ownerEmail}</TableCell>
                  <TableCell>{formatDate(monitor.createdAt)}</TableCell>
                  <TableCell>{formatDate(monitor.lastCheckedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={data?.total ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}

export default AdminMonitorsTable;
